import { useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';
import { ordersFor, quotesFor, skusFor, commsFor, reorderSignal, isDemoQuery, usd } from '../lib/clientDeepData';
import type { Account } from '../lib/accounts';

type Action = 'summary' | 'email' | 'actions' | 'missing';
const ACTIONS: { k: Action; label: string }[] = [
  { k: 'summary', label: 'Summarize client history' }, { k: 'email', label: 'Draft follow-up email' },
  { k: 'actions', label: 'Suggest next actions' }, { k: 'missing', label: 'Detect missing quote data' },
];

export default function SalesAI() {
  const { demo } = useAuth();
  const { data } = useAccounts(demo);
  const clients = useMemo(() => (data?.accounts ?? []).filter((a) => a.type !== 'vendor'), [data]);
  const [name, setName] = useState('');
  const [out, setOut] = useState('Pick a client and choose an action. The assistant reads their orders, quotes, SKUs, and communications.');

  const client: Account | undefined = clients.find((c) => c.name.toLowerCase() === name.toLowerCase()) || (name ? undefined : clients[0]);

  function run(a: Action) {
    if (!client) { setOut('Select a valid client first.'); return; }
    const id = client.id, nm = client.name;
    const demo = isDemoQuery();
    const orders = demo ? ordersFor(id) : [], quotes = demo ? quotesFor(id) : [], skus = demo ? skusFor(id) : [], comms = demo ? commsFor(id) : [];
    const rs = demo ? reorderSignal(id) : { due: false, window: '—', confidence: 0 };
    const openVal = orders.reduce((s, o) => s + o.value, 0) + quotes.reduce((s, q) => s + q.value, 0);
    if (a === 'summary') {
      setOut(`CLIENT SUMMARY — ${nm}\n\nIndustry: ${client.industry || 'n/a'} · ${client.billing?.city || ''} ${client.billing?.state || ''}\nOpen value: ${usd(openVal)} across ${orders.length} order(s) and ${quotes.length} quote(s).\nActive SKUs: ${skus.length} (top: ${skus[0]?.sku || 'n/a'}, ${skus[0]?.reorders || 0}× reorders).\nRecent communications: ${comms.length} logged.\nReorder signal: ${rs.due ? `${rs.window} (${rs.confidence}% confidence)` : 'none'}.\n\nRelationship read: ${client.healthScore && client.healthScore >= 80 ? 'Healthy and active' : 'Needs attention'}. Priority is keeping open quotes moving and protecting due dates.`);
    } else if (a === 'email') {
      const q = quotes[0];
      setOut(`DRAFT — Follow-up email to ${client.contactName || 'the buyer'} at ${nm}\n\nSubject: Quick follow-up on your ${q ? q.product : 'recent'} quote\n\nHi ${(client.contactName || '').split(' ')[0] || 'there'},\n\nThanks again for the opportunity. I wanted to follow up on ${q ? `quote ${q.id} for ${q.qty} of ${q.product} (${usd(q.value)})` : 'your recent request'}. If the pricing and timing work, I can convert this to a sales order today and lock in your production slot.\n\nIs there anything you need from us — updated SKUs, artwork, or a revised quantity — to move forward?\n\nBest,\nFatima · Microflex Film`);
    } else if (a === 'actions') {
      const lines = [
        quotes.length ? `Follow up on ${quotes.length} open quote(s) — ${usd(quotes.reduce((s, q) => s + q.value, 0))} in play.` : 'No open quotes — propose a reorder or new SKU.',
        rs.due ? `Reorder signal is ${rs.window.toLowerCase()} — pitch ${rs.sku?.sku} now.` : 'No reorder due; nurture with a check-in.',
        orders.length ? `Protect ${orders.length} active order(s) — confirm material and due dates.` : 'No active orders.',
        comms.length === 0 ? 'No recent contact — reach out this week.' : 'Recent contact logged — keep cadence.',
      ];
      setOut(`NEXT BEST ACTIONS — ${nm}\n\n${lines.map((l, i) => `${i + 1}. ${l}`).join('\n')}`);
    } else {
      const miss: string[] = [];
      if (!client.email) miss.push('Contact email');
      if (!client.phone) miss.push('Phone number');
      if (!client.industry) miss.push('Industry');
      if (!skus.length) miss.push('SKU specifications');
      if (!client.shipping?.street) miss.push('Shipping address');
      setOut(`MISSING QUOTE DATA — ${nm}\n\n${miss.length ? miss.map((m) => `• ${m}`).join('\n') : '• None — record looks complete for quoting.'}\n\n${miss.length ? 'Collect these before sending the next quote to avoid rework.' : 'Ready to quote.'}`);
    }
  }

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">AI Sales Assistant</div><div className="page-sub">Summarize client history, draft emails, suggest next actions, and detect missing quote data.</div></div>
        <span className="live-flag">Beta</span>
      </div>
      <div className="grid sidebar-layout">
        <div className="card">
          <div className="card-header"><div><h3>Assistant</h3><p>Scoped to one client's record.</p></div></div>
          <div className="card-body">
            <div className="field" style={{ marginBottom: 12 }}><label>Client</label><input list="ai-clients" value={name} onChange={(e) => setName(e.target.value)} placeholder={clients[0]?.name || 'Search client…'} /><datalist id="ai-clients">{clients.slice(0, 200).map((c) => <option key={c.id} value={c.name} />)}</datalist></div>
            <div className="qa-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              {ACTIONS.map((a) => <button key={a.k} className="qa" onClick={() => run(a.k)}>{a.label}</button>)}
            </div>
            <div className="ai-output" style={{ marginTop: 14, minHeight: 280 }}>{out}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><div><h3>How it works</h3><p>Human-in-the-loop.</p></div></div>
          <div className="card-body">
            <ul className="nba">
              <li>Reads the selected client's orders, quotes, SKUs, and communications.</li>
              <li>Drafts are suggestions — review and edit before sending.</li>
              <li>Flags missing data that would slow a quote.</li>
              <li>Live wiring will use Gemini with function calling over Firestore.</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
